import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Bell, AlertTriangle, AlertCircle, Info, CheckCircle, X, Filter, Search, Clock } from 'lucide-react';
import Header from '../../../components/ui/Header';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const SystemAlerts = () => {
  const navigate = useNavigate();
  const [user] = useState({
    id: 'admin-001',
    name: 'Sarah Johnson',
    email: 'sarah.johnson@moneytransfer.com',
    role: 'admin'
  });

  const [selectedSeverity, setSelectedSeverity] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('active');
  const [searchQuery, setSearchQuery] = useState('');

  const handleLogout = () => {
    navigate('/login');
  };

  // Mock alert statistics
  const alertStats = [
    {
      title: 'Active Alerts',
      value: '23',
      change: '+5',
      changeType: 'neutral',
      icon: Bell,
      color: 'text-primary',
      bgColor: 'bg-primary/10'
    },
    {
      title: 'Critical',
      value: '7',
      change: '+2',
      changeType: 'negative',
      icon: AlertTriangle,
      color: 'text-error',
      bgColor: 'bg-error/10'
    },
    {
      title: 'Resolved Today',
      value: '34',
      change: '+12',
      changeType: 'positive',
      icon: CheckCircle,
      color: 'text-success',
      bgColor: 'bg-success/10'
    },
    {
      title: 'Avg Response Time',
      value: '12m',
      change: '-3m',
      changeType: 'positive',
      icon: Clock,
      color: 'text-accent',
      bgColor: 'bg-accent/10'
    }
  ];

  const severityOptions = [
    { value: 'all', label: 'All Severities' },
    { value: 'critical', label: 'Critical' },
    { value: 'high', label: 'High' },
    { value: 'medium', label: 'Medium' },
    { value: 'low', label: 'Low' },
    { value: 'info', label: 'Informational' }
  ];

  const typeOptions = [
    { value: 'all', label: 'All Types' },
    { value: 'security', label: 'Security' },
    { value: 'performance', label: 'Performance' },
    { value: 'compliance', label: 'Compliance' },
    { value: 'system', label: 'System' },
    { value: 'transaction', label: 'Transaction' },
    { value: 'integration', label: 'Integration' }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'acknowledged', label: 'Acknowledged' },
    { value: 'resolved', label: 'Resolved' },
    { value: 'dismissed', label: 'Dismissed' }
  ];

  // Mock alerts data
  const [alerts, setAlerts] = useState([
    {
      id: 'ALERT-001',
      type: 'security',
      severity: 'critical',
      title: 'Multiple Failed Login Attempts',
      message: 'Detected 15+ failed login attempts from IP 192.168.1.100 within 5 minutes',
      timestamp: new Date(Date.now() - 300000),
      status: 'active',
      source: 'Authentication Service',
      affectedUsers: '3',
      actionRequired: true
    },
    {
      id: 'ALERT-002',
      type: 'compliance',
      severity: 'high',
      title: 'AML Threshold Exceeded',
      message: 'Customer CUST-789 has exceeded daily transaction limit of $50,000',
      timestamp: new Date(Date.now() - 600000),
      status: 'acknowledged',
      source: 'Transaction Monitoring',
      affectedUsers: '1',
      actionRequired: true
    },
    {
      id: 'ALERT-003',
      type: 'system',
      severity: 'critical',
      title: 'Database Connection Pool Exhausted',
      message: 'Connection pool reached maximum capacity (100/100). Performance degradation expected.',
      timestamp: new Date(Date.now() - 900000),
      status: 'active',
      source: 'Database Service',
      affectedUsers: 'All',
      actionRequired: true
    },
    {
      id: 'ALERT-004',
      type: 'performance',
      severity: 'medium',
      title: 'API Response Time Degradation',
      message: 'Average API response time increased to 2.5s (normal: <500ms)',
      timestamp: new Date(Date.now() - 1200000),
      status: 'acknowledged',
      source: 'API Gateway',
      affectedUsers: 'All',
      actionRequired: false
    },
    {
      id: 'ALERT-005',
      type: 'transaction',
      severity: 'high',
      title: 'Unusual Transaction Pattern',
      message: 'Detected rapid succession of small transactions to same recipient - possible structuring',
      timestamp: new Date(Date.now() - 1500000),
      status: 'active',
      source: 'Fraud Detection',
      affectedUsers: '2',
      actionRequired: true
    },
    {
      id: 'ALERT-006',
      type: 'integration',
      severity: 'medium',
      title: 'Payment Gateway Timeout',
      message: 'Stripe API timeout detected - 3 failed payment processing attempts',
      timestamp: new Date(Date.now() - 1800000),
      status: 'resolved',
      source: 'Payment Service',
      affectedUsers: '5',
      actionRequired: false
    },
    {
      id: 'ALERT-007',
      type: 'compliance',
      severity: 'critical',
      title: 'Sanctions List Match',
      message: 'Potential OFAC sanctions list match for recipient in transaction TXN-2024-456',
      timestamp: new Date(Date.now() - 2100000),
      status: 'active',
      source: 'Sanctions Screening',
      affectedUsers: '1',
      actionRequired: true
    },
    {
      id: 'ALERT-008',
      type: 'security',
      severity: 'high',
      title: 'Suspicious IP Activity',
      message: 'New login from high-risk country IP address - Ghana (GH)',
      timestamp: new Date(Date.now() - 2400000),
      status: 'acknowledged',
      source: 'Security Service',
      affectedUsers: '1',
      actionRequired: true
    },
    {
      id: 'ALERT-009',
      type: 'system',
      severity: 'low',
      title: 'Scheduled Maintenance Window',
      message: 'System maintenance scheduled for November 22, 2024 at 02:00 UTC',
      timestamp: new Date(Date.now() - 86400000),
      status: 'active',
      source: 'System Administrator',
      affectedUsers: 'All',
      actionRequired: false
    },
    {
      id: 'ALERT-010',
      type: 'performance',
      severity: 'info',
      title: 'High Traffic Volume',
      message: 'Current traffic is 150% of average - all systems operating normally',
      timestamp: new Date(Date.now() - 3600000),
      status: 'active',
      source: 'Load Balancer',
      affectedUsers: 'All',
      actionRequired: false
    }
  ]);

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical':
        return AlertTriangle;
      case 'high':
        return AlertCircle;
      case 'medium':
        return Info;
      case 'low':
        return Info;
      case 'info':
        return Info;
      default:
        return Info;
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical':
        return 'text-error bg-error/10 border-error/20';
      case 'high':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'medium':
        return 'text-blue-500 bg-blue-500/10 border-blue-500/20';
      case 'low':
        return 'text-muted-foreground bg-muted/50 border-muted';
      case 'info':
        return 'text-primary bg-primary/10 border-primary/20';
      default:
        return 'text-muted-foreground bg-muted/50 border-muted';
    }
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      active: 'bg-error/10 text-error border-error/20',
      acknowledged: 'bg-warning/10 text-warning border-warning/20',
      resolved: 'bg-success/10 text-success border-success/20',
      dismissed: 'bg-muted text-muted-foreground border-muted'
    };

    return statusStyles?.[status] || statusStyles?.active;
  };

  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const diff = Math.floor((now - timestamp) / 1000); // seconds

    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const handleAlertAction = (alertId, action) => {
    console.log(`Performing ${action} on alert ${alertId}`);
    
    if (action === 'acknowledge') {
      setAlerts(alerts?.map(alert => 
        alert?.id === alertId ? { ...alert, status: 'acknowledged' } : alert
      ));
    } else if (action === 'resolve') {
      setAlerts(alerts?.map(alert => 
        alert?.id === alertId ? { ...alert, status: 'resolved' } : alert
      ));
    } else if (action === 'dismiss') {
      setAlerts(alerts?.map(alert => 
        alert?.id === alertId ? { ...alert, status: 'dismissed' } : alert
      ));
    }
  };

  const handleViewDetails = (alertId) => {
    console.log(`Viewing details for alert ${alertId}`);
    // Navigate to detailed alert view or open modal
  };

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} onLogout={handleLogout} />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/admin-dashboard')}
            className="flex items-center text-muted-foreground hover:text-foreground mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Admin Dashboard
          </button>
          
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-foreground">System Alerts</h1>
              <p className="text-muted-foreground mt-2">
                Monitor and manage system alerts, security incidents, and compliance notifications
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <div className="w-3 h-3 rounded-full bg-success animate-pulse"></div>
                All systems operational
              </div>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {alertStats?.map((stat, idx) => (
            <div key={idx} className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground">{stat?.title}</p>
                  <p className="text-3xl font-bold text-foreground mt-2">{stat?.value}</p>
                  <p className={`text-sm mt-2 ${
                    stat?.changeType === 'positive' ? 'text-success' : 
                    stat?.changeType === 'negative'? 'text-error' : 'text-muted-foreground'
                  }`}>
                    {stat?.change} from last hour
                  </p>
                </div>
                <div className={`${stat?.bgColor} p-3 rounded-lg`}>
                  <stat.icon className={`w-6 h-6 ${stat?.color}`} />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-card rounded-lg border border-border p-6 mb-6">
          <div className="flex items-center mb-4">
            <Filter className="w-5 h-5 text-muted-foreground mr-2" />
            <h3 className="text-lg font-semibold text-foreground">Filters</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search alerts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="pl-10"
              />
            </div>
            
            <Select
              value={selectedSeverity}
              onChange={(e) => setSelectedSeverity(e?.target?.value)}
            >
              {severityOptions?.map((option) => (
                <option key={option?.value} value={option?.value}>
                  {option?.label}
                </option>
              ))}
            </Select>
            
            <Select
              value={selectedType}
              onChange={(e) => setSelectedType(e?.target?.value)}
            >
              {typeOptions?.map((option) => (
                <option key={option?.value} value={option?.value}>
                  {option?.label}
                </option>
              ))}
            </Select>
            
            <Select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e?.target?.value)}
            >
              {statusOptions?.map((option) => (
                <option key={option?.value} value={option?.value}>
                  {option?.label}
                </option>
              ))}
            </Select>
          </div>
        </div>

        {/* Alerts List */}
        <div className="space-y-4">
          {alerts?.map((alert) => {
            const SeverityIcon = getSeverityIcon(alert?.severity);
            
            return (
              <div
                key={alert?.id}
                className="bg-card rounded-lg border border-border p-6 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-start gap-4">
                  {/* Severity Icon */}
                  <div className={`p-3 rounded-lg border ${getSeverityColor(alert?.severity)}`}>
                    <SeverityIcon className="w-6 h-6" />
                  </div>

                  {/* Alert Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-foreground">{alert?.title}</h3>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSeverityColor(alert?.severity)}`}>
                            {alert?.severity?.toUpperCase()}
                          </span>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusBadge(alert?.status)}`}>
                            {alert?.status?.charAt(0)?.toUpperCase() + alert?.status?.slice(1)}
                          </span>
                          {alert?.actionRequired && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-warning/10 text-warning border border-warning/20">
                              Action Required
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-foreground mb-3">{alert?.message}</p>
                        
                        <div className="flex items-center gap-6 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatTimestamp(alert?.timestamp)}
                          </span>
                          <span>ID: {alert?.id}</span>
                          <span>Type: {alert?.type?.charAt(0)?.toUpperCase() + alert?.type?.slice(1)}</span>
                          <span>Source: {alert?.source}</span>
                          <span>Affected: {alert?.affectedUsers} user(s)</span>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-2 mt-4">
                      {alert?.status === 'active' && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleAlertAction(alert?.id, 'acknowledge')}
                            className="text-xs"
                          >
                            Acknowledge
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleAlertAction(alert?.id, 'resolve')}
                            className="text-xs"
                          >
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Resolve
                          </Button>
                        </>
                      )}
                      {alert?.status === 'acknowledged' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleAlertAction(alert?.id, 'resolve')}
                          className="text-xs"
                        >
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Resolve
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewDetails(alert?.id)}
                        className="text-xs"
                      >
                        View Details
                      </Button>
                      {(alert?.status === 'active' || alert?.status === 'acknowledged') && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleAlertAction(alert?.id, 'dismiss')}
                          className="text-xs text-muted-foreground"
                        >
                          <X className="w-3 h-3 mr-1" />
                          Dismiss
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* No Results */}
        {alerts?.length === 0 && (
          <div className="bg-card rounded-lg border border-border p-12 text-center">
            <Bell className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">No Alerts Found</h3>
            <p className="text-muted-foreground">
              There are no alerts matching your current filters.
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default SystemAlerts;